import React from 'react';
import calViewMap from '../Index';
import { getValueFromKey } from '../../docAutomation/formFunctions';
import SarasPopup from '../../../../library/container/popup/SarasPopup';
import SarasDialog from '../../../../library/container/popup/SarasDialogDefAction';
import SarasTrans from '../../../../library/notifications/SarasTrans';
import SarasButton from '../../../../library/input/buttons/button/SarasButton';

const CalControlView = ({ viewData, calendarControl, onFocusClick }) => {
    const [otpValue, setOtpValue] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [unlockFlowState, setUnlockFlowState] = React.useState('idle');
    const [otpRequestState, setOtpRequestState] = React.useState('idle');
    const settingsAnchorRef = React.useRef(null);

    const responseViewData = getValueFromKey(viewData, 'viewData', null);
    const viewKey = getValueFromKey(viewData, 'viewKey', '');
    const DynamicComponent = viewKey ? calViewMap[viewKey] : null;
    const title = 'Planning Settings';
    const currentPlanLockMode = calendarControl?.planLockMode
        || getValueFromKey(responseViewData, 'planLockMode', 'none')
        || 'none';
    const requiresPlanLocking = currentPlanLockMode !== 'none';
    const isPlanLocked = currentPlanLockMode === 'locked';

    if (!responseViewData || !DynamicComponent) return null;

    const renderOtpStatusMessage = () => {
        if (otpRequestState === 'sending') {
            return { text: 'Sending OTP', color: '#2f6fed' };
        }

        if (otpRequestState === 'sent') {
            return { text: 'OTP sent', color: '#2f8f5b' };
        }

        if (otpRequestState === 'verifying') {
            return { text: 'Verifying OTP', color: '#2f6fed' };
        }

        if (otpRequestState === 'request_failed') {
            return { text: 'Failed to send OTP', color: '#c24646' };
        }

        if (otpRequestState === 'verify_failed') {
            return { text: 'Invalid OTP', color: '#c24646' };
        }

        return null;
    };

    const otpStatusMessage = renderOtpStatusMessage();

    const openSettings = () => {
        setUnlockFlowState('idle');
        setOtpRequestState('idle');
        setOpen(true);
    };

    const onDialogClose = () => {
        setUnlockFlowState('idle');
        setOtpRequestState('idle');
        setOtpValue('');
        setOpen(false);
    };

    const onLockActionClick = () => {
        if (!calendarControl) return;

        if (!isPlanLocked) {
            setOpen(true);
            setUnlockFlowState('lock');
            setOtpRequestState('idle');
            setOtpValue('');
            return;
        }

        setOpen(true);
        setUnlockFlowState('request');
        setOtpRequestState('idle');
    };

    const onLockConfirm = async () => {
        if (!calendarControl?.lockPlan) return;

        const response = await calendarControl.lockPlan();

        if (response?.data?.isError === 1) {
            return;
        }

        setUnlockFlowState('idle');
        setOtpRequestState('idle');
        setOtpValue('');
        setOpen(false);
    };

    const onOtpSend = async () => {
        if (!calendarControl?.requestUnlockOtp) return;

        setOtpRequestState('sending');

        try {
            const response = await calendarControl.requestUnlockOtp();

            if (response?.data?.isError === 1) {
                setOtpRequestState('request_failed');
                return;
            }

            setOtpRequestState('sent');
            setUnlockFlowState('verify');
        }
        catch (error) {
            setOtpRequestState('request_failed');
        }
    };

    const onOtpVerify = async () => {
        if (!calendarControl?.verifyUnlockOtp) return;

        setOtpRequestState('verifying');

        try {
            const response = await calendarControl.verifyUnlockOtp(otpValue);

            if (response?.data?.isError === 1) {
                setOtpRequestState('verify_failed');
                return;
            }

            calendarControl.unlockPlan();
            setUnlockFlowState('idle');
            setOtpRequestState('idle');
            setOtpValue('');
            setOpen(false);
        }
        catch (error) {
            setOtpRequestState('verify_failed');
        }
    };

    return (
        <>
            <div className="uiCalGridFloatingAction uiCalGridFloatingActionSurface">
                {requiresPlanLocking ? (
                    <SarasButton
                        onClick={onLockActionClick}
                        starIcon={isPlanLocked ? 'locked' : 'lockopen'}
                        starIconClassName={isPlanLocked ? 'uiRed' : 'uiGreen'}
                        onlyIcon={true}
                        tooltip={isPlanLocked ? 'Plan is locked' : 'Plan is unlocked'}
                        className="uiLink uiIcon"
                    />
                ) : null}
                <span ref={settingsAnchorRef}>
                    <SarasButton
                        onClick={openSettings}
                        starIcon="settings"
                        onlyIcon={true}
                        tooltip="Planning Settings"
                        className="uiLink uiIcon"
                    />
                </span>
                <SarasButton
                    onClick={onFocusClick}
                    starIcon="focus"
                    onlyIcon={true}
                    tooltip="Move focus to current month"
                    className="uiLink uiIcon"
                />
            </div>

            <SarasPopup anchorRef={settingsAnchorRef} open={open} onClose={onDialogClose}>
                <SarasDialog
                    formStyle={{ width: '340px' }}
                    contentStyle={{ padding: '0', overflow: 'hidden' }}
                    title={<SarasTrans value={title} />}
                    closeName='Close'
                    closeFunction={onDialogClose}
                >
                    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {requiresPlanLocking && unlockFlowState === 'lock' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ color: '#5e708c' }}>
                                    <SarasTrans value='Lock plan to make it read only' />
                                </div>
                                <div className='uiFlexBox' style={{ justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ color: '#5e708c', fontSize: '0.9em' }}>
                                        <SarasTrans value='Confirm lock' />
                                    </div>
                                    <SarasButton
                                        className='uiSaveAction'
                                        onClick={onLockConfirm}
                                    >
                                        <SarasTrans value='Lock Plan' />
                                    </SarasButton>
                                </div>
                            </div>
                        ) : null}

                        {requiresPlanLocking && unlockFlowState === 'request' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ color: '#5e708c' }}>
                                    <SarasTrans value='Send OTP to unlock plan' />
                                </div>
                                <div className='uiFlexBox' style={{ justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ color: otpStatusMessage ? otpStatusMessage.color : '#5e708c', fontSize: '0.9em' }}>
                                        {otpStatusMessage ? <SarasTrans value={otpStatusMessage.text} /> : null}
                                    </div>
                                <SarasButton
                                    className='uiSaveAction'
                                    onClick={onOtpSend}
                                    disabled={otpRequestState === 'sending'}
                                >
                                    <SarasTrans
                                        value={otpRequestState === 'sending' ? 'Sending OTP' : 'Send OTP'}
                                    />
                                </SarasButton>
                                </div>
                            </div>
                        ) : null}

                        {requiresPlanLocking && unlockFlowState === 'verify' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ color: '#5e708c' }}>
                                    <SarasTrans value='Enter OTP to unlock plan' />
                                </div>
                                <input
                                    value={otpValue}
                                    onChange={(event) => setOtpValue(event.target.value)}
                                    placeholder='Enter OTP'
                                    className='uiFormBox'
                                />
                                <div className='uiFlexBox' style={{ justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ color: otpStatusMessage ? otpStatusMessage.color : '#5e708c', fontSize: '0.9em' }}>
                                        {otpStatusMessage ? <SarasTrans value={otpStatusMessage.text} /> : null}
                                    </div>
                                    <SarasButton
                                        className='uiSaveAction'
                                        starIcon='confirm'
                                        onClick={onOtpVerify}
                                        disabled={otpRequestState === 'verifying'}
                                    >
                                        Verify OTP
                                    </SarasButton>
                                </div>
                            </div>
                        ) : null}

                        <DynamicComponent viewData={responseViewData} parentViewData={viewData} calendarControl={calendarControl} />
                    </div>
                </SarasDialog>
            </SarasPopup>
        </>
    );
};

export default CalControlView;
